<template>
  <form
    class="v3-form behaviour-redeem-reward-form"
    data-testid="behaviour-redeem-reward-form"
    @submit.prevent="onSubmit"
  >
    <InfoBox
      v-if="state.serverMessage"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      :message="state.serverMessage"
      announce-assertive
    />

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-redeem-points-lbl"
        html-for="behaviour-redeem-points-input"
      >
        {{ fieldLabels.pointsUsed }}
      </Label>
      <InputField
        id="behaviour-redeem-points-input"
        label-id="behaviour-redeem-points-lbl"
        class="w-100"
        input-type="number"
        :model-value="state.form.pointsUsed"
        :invalid-feedback="errors.pointsUsed"
        data-testid="behaviour-redeem-points-input"
        @update:model-value="(v) => patchForm('pointsUsed', v)"
      />
    </div>

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-redeem-reason-lbl"
        html-for="behaviour-redeem-reason-input"
      >
        {{ $t("behaviour_manage.reward_reason_label") }}
      </Label>
      <InputField
        id="behaviour-redeem-reason-input"
        label-id="behaviour-redeem-reason-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.rewardReason"
        :invalid-feedback="errors.rewardReason"
        data-testid="behaviour-redeem-reason-input"
        @update:model-value="(v) => patchForm('rewardReason', v)"
      />
    </div>

    <BehaviourSelectStudentsFieldset
      v-model="state.form.studentIds"
      class="mb-3"
      :invalid-feedback="errors.studentIds"
      label-id="behaviour-redeem-students-lbl"
      input-id="behaviour-redeem-students-input"
      data-testid="behaviour-redeem-students-select"
    />

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-redeem-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        data-testid="behaviour-redeem-submit"
      >
        {{ $t("behaviour_manage.redeem_submit") }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import BehaviourSelectStudentsFieldset from "@/v3components/Forms/Behaviour/BehaviourSelectStudentsFieldset.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import {
  normalizeBehaviourStudentIds,
  parseBehaviourPointsApiError
} from "@/helpers/behaviourPointsForm.js"
import { computed, inject, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourRedeemRewardForm",
  components: {
    BehaviourSelectStudentsFieldset,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const fieldLabels = computed(() => ({
      pointsUsed: t("behaviour_manage.points_used_label", {
        points: behaviourTerminology.value.points
      })
    }))

    const state = reactive({
      form: {
        pointsUsed: "",
        rewardReason: "",
        studentIds: []
      },
      apiFieldErrors: {},
      isProcessing: false,
      isServerError: false,
      serverTitle: "",
      serverMessage: "",
      touched: false
    })

    const clientErrors = computed(() => {
      if (!state.touched) return {}
      const out = {}
      if (!String(state.form.pointsUsed ?? "").trim()) {
        out.pointsUsed = t("behaviour_manage.error_required_field", {
          field: fieldLabels.value.pointsUsed
        })
      }
      if (!String(state.form.rewardReason ?? "").trim()) {
        out.rewardReason = t("behaviour_manage.error_required_field", {
          field: t("behaviour_manage.reward_reason_label")
        })
      }
      if (!normalizeBehaviourStudentIds(state.form.studentIds).length) {
        out.studentIds = t("behaviour_manage.error_select_students")
      }
      return out
    })

    const errors = computed(() => ({
      ...clientErrors.value,
      ...state.apiFieldErrors
    }))

    const patchForm = (key, value) => {
      state.form[key] = value ?? (key === "studentIds" ? [] : "")
      if (state.apiFieldErrors[key]) {
        const next = { ...state.apiFieldErrors }
        delete next[key]
        state.apiFieldErrors = next
      }
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""
    }

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""

      if (Object.keys(clientErrors.value).length) return

      try {
        state.isProcessing = true
        const result = await store.dispatch(
          "behaviourManage/createPointsRedemption",
          state.form
        )
        modal?.setAction?.("pointHistoryRedeemed", {
          ...state.form,
          ...result
        })
        emit("cancel")
      } catch (error) {
        const parsed = parseBehaviourPointsApiError(error, "redeem")
        if (Object.keys(parsed.fieldErrors).length) {
          state.apiFieldErrors = parsed.fieldErrors
        }

        const normalized = parsed.message.toLowerCase()
        const pointsTerm = behaviourTerminology.value.points
        state.isServerError = true
        state.serverTitle = t("behaviour_manage.error_title")
        state.serverMessage =
          normalized.includes("enough points") ||
          normalized.includes("insufficient")
            ? t("behaviour_manage.insufficient_points_error")
            : parsed.message ||
              t("behaviour_manage.redeem_error", { points: pointsTerm })
      } finally {
        state.isProcessing = false
      }
    }

    return {
      fieldLabels,
      state,
      errors,
      patchForm,
      onCancel,
      onSubmit
    }
  }
}
</script>
